/**
 * Admin Dashboard
 * Overview of platform statistics
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Building2, Briefcase, FileText,
  TrendingUp, Mail, UserPlus, Activity, Loader2
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

interface DashboardStats {
  tenants: { total: number; new_this_month: number };
  users: { total: number; active_today: number; active_this_week: number };
  content: { cases: number; documents: number };
  beta: { pending: number; sent: number; registered: number };
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchDashboard();
  }, []);
  
  const fetchDashboard = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon: Icon, 
    color = 'violet',
    delay = 0 
  }: { 
    title: string; 
    value: number | string; 
    subtitle?: string; 
    icon: any; 
    color?: string;
    delay?: number;
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-slate-500 text-sm mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 bg-${color}-500/20 rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
      </div>
    </motion.div>
  );
  
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">Oversikt over Lexy-plattformen</p>
      </div>
      
      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Totalt firmaer"
          value={stats?.tenants.total || 0}
          subtitle={`+${stats?.tenants.new_this_month || 0} denne måneden`}
          icon={Building2}
          color="violet"
          delay={0}
        />
        <StatCard
          title="Totalt brukere"
          value={stats?.users.total || 0}
          subtitle={`${stats?.users.active_today || 0} aktive i dag`}
          icon={Users}
          color="blue"
          delay={0.1}
        />
        <StatCard
          title="Saker"
          value={stats?.content.cases || 0}
          icon={Briefcase}
          color="emerald"
          delay={0.2}
        />
        <StatCard
          title="Dokumenter"
          value={stats?.content.documents || 0}
          icon={FileText}
          color="amber"
          delay={0.3}
        />
      </div>
      
      {/* Beta program section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-br from-violet-600/20 to-indigo-600/20 border border-violet-500/30 rounded-2xl p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center">
            <Mail className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Beta-program</h2>
            <p className="text-sm text-slate-400">Invitasjoner og registreringer</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-400">{stats?.beta.pending || 0}</p>
            <p className="text-sm text-slate-400 mt-1">Venter</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-400">{stats?.beta.sent || 0}</p>
            <p className="text-sm text-slate-400 mt-1">Sendt</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-400">{stats?.beta.registered || 0}</p>
            <p className="text-sm text-slate-400 mt-1">Registrert</p>
          </div>
        </div>
      </motion.div>
      
      {/* Activity feed placeholder */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-8 bg-slate-900 border border-slate-800 rounded-2xl p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <Activity className="w-5 h-5 text-slate-400" />
          <h3 className="font-semibold text-white">Nylig aktivitet</h3>
        </div>
        <p className="text-slate-500 text-sm">Aktivitetslogg kommer snart...</p>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
